import { useState, useEffect, useCallback } from 'react';
import { BrandSource, BrandEnrichmentResult } from '@agency-smm/shared';
import { apiClient } from '../../services/apiClient';

// Hook del knowledge base de fuentes (tab "Fuentes"). Posee la lista de fuentes
// del workspace y las acciones CRUD + el disparo del enriquecimiento. El diff y
// el merge en el form viven afuera (EnrichReviewModal + util brandEnrichment),
// para no acoplar este hook al form.

export interface AddSourceInput {
  kind: 'url' | 'social' | 'text';
  url?: string;
  text?: string;
}

export interface UseBrandSourcesResult {
  sources: BrandSource[];
  isLoading: boolean;
  error: string;
  busy: boolean;
  setError: (v: string) => void;
  reload: () => Promise<void>;
  add: (input: AddSourceInput) => Promise<void>;
  addFile: (file: File) => Promise<void>;
  setEnabled: (id: string, enabled: boolean) => Promise<void>;
  pasteText: (id: string, text: string) => Promise<void>;
  remove: (id: string) => Promise<void>;
  enrich: () => Promise<BrandEnrichmentResult | null>;
}

export function useBrandSources(workspaceId: string | undefined): UseBrandSourcesResult {
  const [sources, setSources] = useState<BrandSource[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!workspaceId) return;
    setIsLoading(true);
    try {
      const data = await apiClient.get<BrandSource[]>(`/api/workspaces/${workspaceId}/sources`);
      setSources(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load sources:', err);
      setError(err instanceof Error ? err.message : 'Failed to load sources');
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { void reload(); }, [reload]);

  const add = useCallback(async (input: AddSourceInput) => {
    if (!workspaceId) return;
    setError('');
    setBusy(true);
    try {
      const created = await apiClient.post<BrandSource>(`/api/workspaces/${workspaceId}/sources`, input);
      setSources((prev) => [...prev, created]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add source');
      throw err;
    } finally {
      setBusy(false);
    }
  }, [workspaceId]);

  const addFile = useCallback(async (file: File) => {
    if (!workspaceId) return;
    setError('');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const created = await apiClient.upload<BrandSource>(`/api/workspaces/${workspaceId}/sources/upload`, fd);
      setSources((prev) => [...prev, created]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload file');
      throw err;
    } finally {
      setBusy(false);
    }
  }, [workspaceId]);

  const setEnabled = useCallback(async (id: string, enabled: boolean) => {
    if (!workspaceId) return;
    // Optimista: toggle inmediato, revertir si falla.
    setSources((prev) => prev.map((s) => (s.id === id ? { ...s, enabled } : s)));
    try {
      await apiClient.patch(`/api/workspaces/${workspaceId}/sources/${id}`, { enabled });
    } catch (err) {
      setSources((prev) => prev.map((s) => (s.id === id ? { ...s, enabled: !enabled } : s)));
      setError(err instanceof Error ? err.message : 'Failed to update source');
    }
  }, [workspaceId]);

  const pasteText = useCallback(async (id: string, text: string) => {
    if (!workspaceId) return;
    setBusy(true);
    try {
      const updated = await apiClient.patch<BrandSource>(`/api/workspaces/${workspaceId}/sources/${id}`, { text });
      setSources((prev) => prev.map((s) => (s.id === id ? updated : s)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update source');
    } finally {
      setBusy(false);
    }
  }, [workspaceId]);

  const remove = useCallback(async (id: string) => {
    if (!workspaceId) return;
    const prev = sources;
    setSources((curr) => curr.filter((s) => s.id !== id));
    try {
      await apiClient.delete(`/api/workspaces/${workspaceId}/sources/${id}`);
    } catch (err) {
      setSources(prev);
      setError(err instanceof Error ? err.message : 'Failed to delete source');
    }
  }, [workspaceId, sources]);

  const enrich = useCallback(async (): Promise<BrandEnrichmentResult | null> => {
    if (!workspaceId) return null;
    setError('');
    setBusy(true);
    try {
      return await apiClient.post<BrandEnrichmentResult>(`/api/workspaces/${workspaceId}/sources/enrich`, {});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Enrichment failed');
      return null;
    } finally {
      setBusy(false);
    }
  }, [workspaceId]);

  return { sources, isLoading, error, busy, setError, reload, add, addFile, setEnabled, pasteText, remove, enrich };
}
